import { Table } from '@tanstack/react-table';
import { isEqual } from 'lodash';
import { useEffect, useRef, useState } from 'react';

import { Filters } from '@work4all/models/lib/DataProvider';

import { useTableLayoutState } from '../../../../table-layout';
import { BoardIdObserver } from '../BoardColumn';

export const useBoard = ({ getTable }: { getTable: () => Table<unknown> }) => {
  const selectedIdsByStatus = useRef<Record<string, Set<number>>>({});
  const [prefilter, setPrefilter] = useState<Filters>();

  const [layout] = useTableLayoutState();

  useEffect(() => {
    if (layout !== 'board') return;

    getTable().resetRowSelection();

    const subscription = BoardIdObserver.subscribe((event) => {
      if (!event) return;
      const { ids, status } = event;

      // Get current set of IDs for the status
      const currentIdsSet =
        selectedIdsByStatus.current[status] || new Set<number>();

      // Compare new IDs with the current set
      const newIdsSet = new Set(ids);
      const isSame =
        newIdsSet.size === currentIdsSet.size &&
        ids.every((id) => currentIdsSet.has(id));

      if (isSame) {
        return; // Skip updating if the IDs are the same
      }

      // Update the selected IDs for the given status
      selectedIdsByStatus.current[status] = newIdsSet;

      // Generate new prefilter based on all selected IDs across all statuses
      const allSelectedIds = Object.values(selectedIdsByStatus.current).flatMap(
        (set) => Array.from(set)
      );

      const newPrefilter = {
        id: {
          $in: allSelectedIds,
        },
      };
      setPrefilter([newPrefilter]);

      const table = getTable();

      const currentSelection = table.getState().rowSelection;
      const newSelection = Object.fromEntries(
        allSelectedIds.map((id) => [id, true])
      );

      if (!isEqual(currentSelection, newSelection)) {
        table.setRowSelection(newSelection);
      }
    });

    return () => subscription.unsubscribe();
  }, [layout, getTable]);

  const outputPrefilter = layout === 'board' ? prefilter : undefined;
  return { prefilter: outputPrefilter };
};
